import React, { useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { FaDownload, FaExclamationTriangle } from 'react-icons/fa';
import { buildUsageAnalytics, formatDuration, planLabel } from '../config/adminUsageAnalytics';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const SEGMENTS = {
  power: { label: 'Power', pill: 'is-ok' },
  regular: { label: 'Regular', pill: 'is-info' },
  casual: { label: 'Casual', pill: 'is-warn' },
  'sem-acesso': { label: 'Sem acesso', pill: '' },
};
const USERS_PREVIEW = 25;

const formatDate = ts => (ts ? new Date(ts).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-');

function Kpi({ label, value, note }) {
  return (
    <div className="admin-kpi">
      <span>{label}</span>
      <strong>{value}</strong>
      {note && <small>{note}</small>}
    </div>
  );
}

function CardHead({ title, hint, children }) {
  return (
    <div className="admin-card-head">
      <div>
        <h3 className="admin-card-title">{title}</h3>
        {hint && <p className="admin-card-hint">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

const baseChart = {
  chart: { toolbar: { show: false } },
  dataLabels: { enabled: false },
  grid: { borderColor: '#edf2f5' },
  yaxis: { min: 0, forceNiceScale: true, labels: { formatter: value => Math.round(value) } },
};

export default function AdminUsoAnalytics({ eventos, usuarios, periodDays, labelPeriodo, loading, onExport }) {
  const [busca, setBusca] = useState('');
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const [segmento, setSegmento] = useState('todos');

  const analytics = useMemo(
    () => buildUsageAnalytics({ eventos, usuarios, periodDays }),
    [eventos, usuarios, periodDays],
  );
  const { kpis, screens, funnel, transitions, segments, recency, plans, atRisk, daily, hours, weekdays, userRows } = analytics;

  const linhasUsuarios = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return userRows.filter(row => (
      (segmento === 'todos' || row.segment === segmento)
      && (!termo || `${row.nome || ''} ${row.email || ''}`.toLowerCase().includes(termo))
    ));
  }, [userRows, busca, segmento]);
  const linhasVisiveis = mostrarTodos ? linhasUsuarios : linhasUsuarios.slice(0, USERS_PREVIEW);

  const dailyChart = {
    options: {
      ...baseChart,
      chart: { ...baseChart.chart, type: 'line' },
      stroke: { curve: 'smooth', width: [3, 2] },
      colors: ['#168673', '#1d78b5'],
      xaxis: { categories: daily.map(day => day.label), tickAmount: Math.min(daily.length, 15), labels: { style: { fontSize: '10px' } } },
      legend: { position: 'top' },
    },
    series: [
      { name: 'Usuários ativos', data: daily.map(day => day.users) },
      { name: 'Telas abertas', data: daily.map(day => day.visits) },
    ],
  };
  const hoursChart = {
    options: {
      ...baseChart,
      chart: { ...baseChart.chart, type: 'bar' },
      colors: ['#c98a1b'],
      plotOptions: { bar: { borderRadius: 3 } },
      xaxis: { categories: hours.map((_, hour) => `${hour}h`), labels: { style: { fontSize: '10px' } } },
    },
    series: [{ name: 'Telas abertas', data: hours }],
  };
  const weekdayChart = {
    options: {
      ...baseChart,
      chart: { ...baseChart.chart, type: 'bar' },
      colors: ['#6a5fb5'],
      plotOptions: { bar: { borderRadius: 4 } },
      xaxis: { categories: WEEKDAYS },
    },
    series: [{ name: 'Telas abertas', data: weekdays }],
  };

  const maxRecency = Math.max(1, ...recency.map(bucket => bucket.value));

  return (
    <main className="admin-panel-content admin-usage-content">
      <div className="admin-section-heading">
        <div><span>Comportamento dos usuários</span><h2>Uso e engajamento</h2></div>
        <p>
          {loading ? 'Carregando eventos…' : `${kpis.visits.toLocaleString('pt-BR')} telas abertas por ${kpis.active} usuário(s) · ${labelPeriodo}.`}
          {' '}Administradores não entram nas métricas.
        </p>
      </div>

      <div className="admin-kpi-grid">
        <Kpi label="Ativos hoje" value={kpis.activeToday} />
        <Kpi label="Ativos em 7 dias" value={kpis.active7} />
        <Kpi label="Ativos em 30 dias" value={kpis.active30 ?? '—'} note={kpis.active30 === null ? 'Disponível a partir de 30 dias' : null} />
        <Kpi label="Contas sem acesso" value={kpis.dormant} note={`de ${kpis.registered} contas`} />
        <Kpi label="Telas por usuário" value={kpis.visitsPerUser} note="Média entre ativos" />
        <Kpi label="Dias ativos por usuário" value={kpis.activeDaysPerUser} note="Média entre ativos" />
        <Kpi label="Módulos por usuário" value={kpis.modulesPerUser} note="Média de módulos distintos" />
        <Kpi label="Permanência média" value={formatDuration(kpis.avgVisitSeconds)} note="Por tela (máx. 30 min)" />
        <Kpi label="Novos que já usaram" value={`${kpis.newActivated}/${kpis.newUsers}`} note="Cadastros do período com algum módulo" />
      </div>

      <section className="admin-card">
        <CardHead title="Atividade por dia" hint={`Usuários distintos e telas abertas em cada dia (${labelPeriodo.toLowerCase()}).`} />
        <ReactApexChart options={dailyChart.options} series={dailyChart.series} type="line" height={240} />
      </section>

      <section className="admin-card">
        <CardHead
          title="Uso por tela"
          hint="Nomes iguais aos do menu. Adoção = % dos usuários ativos que abriram a tela; recorrentes = voltaram em 2+ dias."
        />
        <div className="admin-table-wrap">
          <table className="admin-table" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th className="is-left">Tela</th>
                <th>Usuários</th>
                <th style={{ minWidth: 110 }}>Adoção</th>
                <th>Telas abertas</th>
                <th>Por usuário</th>
                <th>Recorrentes</th>
                <th>Tempo médio</th>
                <th>Última vez</th>
              </tr>
            </thead>
            <tbody>
              {screens.map(row => (
                <tr key={row.key} style={{ opacity: row.visits === 0 ? 0.5 : 1 }}>
                  <td className="is-left"><strong>{row.label}</strong><small>{row.group}</small></td>
                  <td>{row.users}</td>
                  <td>
                    {row.adoptionRate}%
                    <div className="admin-bar" style={{ '--bar-color': row.color, marginTop: 4 }}><span style={{ width: `${row.adoptionRate}%` }} /></div>
                  </td>
                  <td>{row.visits}</td>
                  <td>{row.users ? row.visitsPerUser : '-'}</td>
                  <td>{row.users ? `${row.recurringUsers} (${row.recurringRate}%)` : '-'}</td>
                  <td>{formatDuration(row.avgSeconds)}</td>
                  <td>{formatDate(row.last)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="admin-grid is-even">
        <section className="admin-card">
          <CardHead title="Funil de ativação" hint="Quanto das contas cadastradas avança em cada nível de uso." />
          {funnel.map(step => (
            <div key={step.key} className="admin-bar-row">
              <div><strong>{step.label}</strong><span>{step.value} <small>({step.rate}%)</small></span></div>
              <div className="admin-bar"><span style={{ width: `${step.rate}%` }} /></div>
            </div>
          ))}
        </section>

        <section className="admin-card">
          <CardHead title="Recência do último acesso" hint="Há quanto tempo cada conta usou o app pela última vez." />
          {recency.map(bucket => (
            <div key={bucket.key} className="admin-bar-row" style={{ '--bar-color': bucket.key === 'nunca' ? '#b4c3cc' : '#168673' }}>
              <div><span>{bucket.label}</span><strong>{bucket.value}</strong></div>
              <div className="admin-bar"><span style={{ width: `${(bucket.value / maxRecency) * 100}%` }} /></div>
            </div>
          ))}
        </section>
      </div>

      <div className="admin-grid is-even">
        <section className="admin-card">
          <CardHead title="Horários de uso" hint="Horário local do navegador do administrador." />
          <ReactApexChart options={hoursChart.options} series={hoursChart.series} type="bar" height={210} />
        </section>
        <section className="admin-card">
          <CardHead title="Dias da semana" hint="Dias com mais telas abertas." />
          <ReactApexChart options={weekdayChart.options} series={weekdayChart.series} type="bar" height={210} />
        </section>
      </div>

      <div className="admin-grid is-even">
        <section className="admin-card">
          <CardHead title="Fluxos mais comuns" hint="Passagens entre telas em até 30 minutos." />
          {transitions.length === 0 && <div className="admin-empty">Sem navegação suficiente no período.</div>}
          {transitions.map(item => (
            <div key={`${item.from}>${item.to}`} className="admin-list-row">
              <span>{item.from} → <b>{item.to}</b></span>
              <b>{item.count}×</b>
            </div>
          ))}
        </section>

        <section className="admin-card">
          <CardHead title="Engajamento por plano" hint="Contas que acessaram dentro de cada plano." />
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th className="is-left">Plano</th><th>Ativas / contas</th><th>Telas por ativo</th></tr>
              </thead>
              <tbody>
                {plans.map(item => (
                  <tr key={item.plan}>
                    <td className="is-left"><strong>{item.plan.replace(/^\w+/, id => planLabel(id))}</strong></td>
                    <td>{item.active}/{item.total} <small style={{ display: 'inline' }}>({item.rate}%)</small></td>
                    <td>{item.active ? item.visitsPerActive : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className={`admin-card ${atRisk.length > 0 ? 'is-attention' : 'is-clear'}`}>
        <CardHead
          title={<><FaExclamationTriangle /> Contas em risco de abandono ({atRisk.length})</>}
          hint="Plano ou teste vigente, com 7+ dias sem acesso. Priorize contato antes do vencimento."
        />
        {atRisk.length === 0 && <div className="admin-empty">Nenhuma conta nessa condição.</div>}
        {atRisk.map(row => (
          <div key={row.id} className="admin-list-row">
            <div><strong>{row.nome || 'Sem nome'}</strong><small>{row.email}</small></div>
            <div className="admin-list-meta">
              <span className="admin-pill is-warn">{planLabel(row.plano)}{row.trial ? ' · teste' : ''}</span>
              <br />vence {new Date(row.planoExpiracao).toLocaleDateString('pt-BR')} · {row.daysSince === null ? 'sem acesso no período' : `último acesso há ${row.daysSince} dias`}
            </div>
          </div>
        ))}
      </section>

      <section className="admin-card">
        <CardHead
          title="Uso por usuário"
          hint={`Power: ${segments.thresholds.power}+ telas abertas · Regular: ${segments.thresholds.regular}+ · Casual: abaixo disso. No período: ${segments.power} power, ${segments.regular} regulares, ${segments.casual} casuais.`}
        >
          <button type="button" className="admin-btn" onClick={() => onExport?.(linhasUsuarios)}><FaDownload /> Exportar</button>
        </CardHead>
        <div className="admin-filters">
          <input type="text" placeholder="Buscar por nome ou e-mail" value={busca} onChange={event => setBusca(event.target.value)} />
          <select value={segmento} onChange={event => setSegmento(event.target.value)} aria-label="Segmento">
            <option value="todos">Todos os segmentos</option>
            {Object.entries(SEGMENTS).map(([key, item]) => <option key={key} value={key}>{item.label}</option>)}
          </select>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th className="is-left">Usuário</th>
                <th>Plano</th>
                <th>Segmento</th>
                <th>Telas abertas</th>
                <th>Dias ativos</th>
                <th>Módulos</th>
                <th>Tela principal</th>
                <th>Tempo total</th>
                <th>Último acesso</th>
              </tr>
            </thead>
            <tbody>
              {linhasVisiveis.length === 0 && <tr><td colSpan={9} className="admin-empty">Nenhum usuário encontrado.</td></tr>}
              {linhasVisiveis.map(row => (
                <tr key={row.id}>
                  <td className="is-left"><strong>{row.nome || 'Sem nome'}</strong><small>{row.email}</small></td>
                  <td><span className="admin-pill">{planLabel(row.plano)}{row.trial ? ' · teste' : ''}</span></td>
                  <td><span className={`admin-pill ${SEGMENTS[row.segment].pill}`}>{SEGMENTS[row.segment].label}</span></td>
                  <td>{row.visits}</td>
                  <td>{row.activeDays}</td>
                  <td>{row.modules}</td>
                  <td>{row.topScreen}</td>
                  <td>{formatDuration(row.seconds)}</td>
                  <td>{formatDate(row.last)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {linhasUsuarios.length > USERS_PREVIEW && (
          <button type="button" className="admin-btn is-ghost" style={{ marginTop: 12 }} onClick={() => setMostrarTodos(value => !value)}>
            {mostrarTodos ? 'Mostrar menos' : `Mostrar todos (${linhasUsuarios.length})`}
          </button>
        )}
      </section>
    </main>
  );
}
